import type { RouteRecordRaw } from 'vue-router';

/**
 * Route metadata type definitions
 */
export interface RouteMetaAuth {
  /**
   * Requires user to be authenticated
   */
  requiresAuth?: boolean;
  /**
   * Requires user to be a guest (not authenticated)
   */
  requiresGuest?: boolean;
  /**
   * Requires user to have verified email
   */
  requiresVerification?: boolean;
  /**
   * Required user role
   */
  role?: string;
  /**
   * Required permissions
   */
  permissions?: string[];
  /**
   * Page title
   */
  title?: string;
  /**
   * Layout component to use
   */
  layout?: 'main' | 'auth' | 'public' | 'admin';
  /**
   * Whether to show in navigation
   */
  showInNav?: boolean;
  /**
   * Navigation order
   */
  navOrder?: number;
  /**
   * Icon for navigation
   */
  navIcon?: string;
  /**
   * Whether route is disabled
   */
  disabled?: boolean;
  /**
   * Custom redirect on access denied
   */
  accessDeniedRedirect?: string;
  /**
   * Whether to track page views
   */
  trackPageView?: boolean;
  /**
   * SEO meta description
   */
  description?: string;
  /**
   * SEO meta keywords
   */
  keywords?: string[];
}

/**
 * Extended route record with typed meta
 * Using intersection type to properly extend RouteRecordRaw
 */
export type TypedRouteRecord = RouteRecordRaw & {
  meta?: RouteMetaAuth;
  children?: TypedRouteRecord[];
};

/**
 * Route helper functions
 */
export class RouteUtils {
  /**
   * Check if route requires authentication
   */
  static requiresAuth(route: RouteRecordRaw): boolean {
    return route.meta?.requiresAuth === true;
  }

  /**
   * Check if route requires guest access
   */
  static requiresGuest(route: RouteRecordRaw): boolean {
    return route.meta?.requiresGuest === true;
  }

  /**
   * Check if route requires email verification
   */
  static requiresVerification(route: RouteRecordRaw): boolean {
    return route.meta?.requiresVerification === true;
  }

  /**
   * Get route title
   */
  static getTitle(route: RouteRecordRaw): string {
    return route.meta?.title || 'Page';
  }

  /**
   * Get route layout
   */
  static getLayout(route: RouteRecordRaw): string {
    return route.meta?.layout || 'main';
  }

  /**
   * Check if route should show in navigation
   */
  static showInNav(route: RouteRecordRaw): boolean {
    return route.meta?.showInNav !== false;
  }

  /**
   * Get navigation order
   */
  static getNavOrder(route: RouteRecordRaw): number {
    return route.meta?.navOrder || 999;
  }

  /**
   * Create a protected route configuration
   */
  static createProtectedRoute(config: TypedRouteRecord): TypedRouteRecord {
    return {
      ...config,
      meta: {
        requiresAuth: true,
        requiresVerification: true, // Add verification requirement for all protected routes
        layout: 'main',
        trackPageView: true,
        ...config.meta,
      },
    };
  }

  /**
   * Create an auth route configuration
   */
  static createAuthRoute(config: TypedRouteRecord): TypedRouteRecord {
    return {
      ...config,
      meta: {
        requiresGuest: true,
        layout: 'auth',
        trackPageView: true,
        ...config.meta,
      },
    };
  }

  /**
   * Create a public route configuration
   */
  static createPublicRoute(config: TypedRouteRecord): TypedRouteRecord {
    return {
      ...config,
      meta: {
        layout: 'public',
        trackPageView: true,
        ...config.meta,
      },
    };
  }

  /**
   * Create an admin route configuration
   */
  static createAdminRoute(config: TypedRouteRecord): TypedRouteRecord {
    return {
      ...config,
      meta: {
        requiresAuth: true,
        role: 'admin',
        layout: 'admin',
        trackPageView: true,
        ...config.meta,
      },
    };
  }
}

/**
 * Common route configurations
 */
export const ROUTE_NAMES = {
  // Public routes
  LANDING: 'landing',
  TERMS: 'terms-of-service',
  PRIVACY: 'privacy-policy',
  ERROR: 'error',
  NOT_FOUND: 'not-found',

  // Auth routes
  LOGIN: 'login',
  REGISTER: 'register',
  FORGOT_PASSWORD: 'forgot-password',
  RESET_PASSWORD: 'reset-password',
  VERIFY_EMAIL: 'verify-email',

  // Protected routes
  DASHBOARD: 'dashboard',
  PROFILE: 'profile',
  API_KEYS: 'api-keys',
  MODEL_GATEWAY: 'model-gateway',
  MCP_GATEWAY: 'mcp-gateway',
  PLAYGROUND: 'playground',
  DOCUMENTATION: 'documentation',
  CHATBOTS: 'chatbots',
  SETTINGS: 'settings',
  ORGANIZATIONS: 'organizations',
  ORGANIZATION_SETTINGS: 'organization-settings',
  ORGANIZATION_CREDITS: 'organization-credits',
  ORGANIZATION_BILLING: 'organization-billing',
  ACCEPT_INVITATION: 'accept-invitation',
  NAVIGATION_DEMO: 'navigation-demo',
  MCP_PLAYGROUND: 'mcp-playground',
  AGENTS: 'agents',
  MEMORIES: 'memories',
  SKILLS: 'skills',
  GUARDRAILS: 'guardrails',
  EVENT_TRIGGERS: 'event-triggers',
  INTEGRATIONS: 'integrations',
  AGENT_CHAT: 'agent-chat',
  AGENT_KERNEL: 'agent-kernel',
  AGENT_WORKFLOW_BUILDER: 'agent-workflow-builder',
  AGENT_WORKFLOW_RUN: 'agent-workflow-run',
  AGENT_VERSIONS: 'agent-versions',
  AGENT_ANALYTICS: 'agent-analytics',
  KERNEL_MESSAGES: 'kernel-messages',
  KERNEL_HOOKS: 'kernel-hooks',
  KERNEL_EVENTS: 'kernel-events',
  KERNEL_PROCESS_MEMORY: 'kernel-process-memory',
  KERNEL_PROCESS_DEBUG: 'kernel-process-debug',
  KERNEL_HANDOFF_TRACE: 'kernel-handoff-trace',
  EXECUTION_REPLAY: 'execution-replay',
  AGENT_WORKFLOW_TEMPLATES: 'agent-workflow-templates',
  MARKETPLACE: 'marketplace',
  APPROVALS: 'approvals',
  CONSOLE: 'console',
  TRACES: 'traces',
  CUSTOM_TOOL_BUILDS: 'mcp-custom-tool-builds',
  CUSTOM_TOOL_BUILD_DETAIL: 'mcp-custom-tool-build-detail',
  CUSTOM_TOOL_VERSIONS: 'mcp-custom-tool-versions',
  CUSTOM_TOOL_NEW_BUILD: 'mcp-custom-tool-new-build',
  BUILDS: 'builds',
  BUILD_DETAIL: 'build-detail',
  LIVE_EXECUTION: 'live-execution',

  // Admin routes
  ADMIN_DASHBOARD: 'admin-dashboard',
  ADMIN_USERS: 'admin-users',
  ADMIN_SETTINGS: 'admin-settings',
} as const;

export type RouteName = (typeof ROUTE_NAMES)[keyof typeof ROUTE_NAMES];

/**
 * Navigation items configuration
 */
export interface NavItem {
  name: string;
  route: RouteName;
  icon?: string;
  order: number;
  requiresAuth?: boolean;
  requiredRole?: string;
  requiredPermissions?: string[];
}

export const NAVIGATION_ITEMS: NavItem[] = [
  {
    name: 'Dashboard',
    route: ROUTE_NAMES.DASHBOARD,
    icon: 'dashboard',
    order: 1,
    requiresAuth: true,
  },
  {
    name: 'Profile',
    route: ROUTE_NAMES.PROFILE,
    icon: 'user',
    order: 2,
    requiresAuth: true,
  },
  {
    name: 'API Keys',
    route: ROUTE_NAMES.API_KEYS,
    icon: 'key',
    order: 3,
    requiresAuth: true,
  },
  {
    name: 'Model Gateway',
    route: ROUTE_NAMES.MODEL_GATEWAY,
    icon: 'server',
    order: 4,
    requiresAuth: true,
  },
  {
    name: 'MCP Gateway',
    route: ROUTE_NAMES.MCP_GATEWAY,
    icon: 'server',
    order: 5,
    requiresAuth: true,
  },
  {
    name: 'Agents',
    route: ROUTE_NAMES.AGENTS,
    icon: 'cpu-chip',
    order: 5.5,
    requiresAuth: true,
  },
  {
    name: 'Console',
    route: ROUTE_NAMES.CONSOLE,
    icon: 'chat',
    order: 5.52,
    requiresAuth: true,
  },
  {
    name: 'Observability',
    route: ROUTE_NAMES.TRACES,
    icon: 'chart-bar',
    order: 5.55,
    requiresAuth: true,
  },
  {
    name: 'Marketplace',
    route: ROUTE_NAMES.MARKETPLACE,
    icon: 'shopping-bag',
    order: 5.6,
    requiresAuth: true,
  },
  {
    name: 'Approvals',
    route: ROUTE_NAMES.APPROVALS,
    icon: 'check-circle',
    order: 5.7,
    requiresAuth: true,
  },
  {
    name: 'Builds',
    route: ROUTE_NAMES.BUILDS,
    icon: 'cube',
    order: 5.75,
    requiresAuth: true,
  },
  {
    name: 'Skills',
    route: ROUTE_NAMES.SKILLS,
    icon: 'academic-cap',
    order: 5.8,
    requiresAuth: true,
  },
  {
    name: 'Guardrails',
    route: ROUTE_NAMES.GUARDRAILS,
    icon: 'shield-check',
    order: 5.85,
    requiresAuth: true,
  },
  {
    name: 'Event Triggers',
    route: ROUTE_NAMES.EVENT_TRIGGERS,
    icon: 'bolt',
    order: 5.87,
    requiresAuth: true,
  },
  {
    name: 'Integrations',
    route: ROUTE_NAMES.INTEGRATIONS,
    icon: 'puzzle-piece',
    order: 5.9,
    requiresAuth: true,
  },
  {
    name: 'API Playground',
    route: ROUTE_NAMES.PLAYGROUND,
    icon: 'beaker',
    order: 6,
    requiresAuth: true,
  },
  {
    name: 'MCP Playground',
    route: ROUTE_NAMES.MCP_PLAYGROUND,
    icon: 'robot',
    order: 7,
    requiresAuth: true,
  },
  {
    name: 'Organizations',
    route: ROUTE_NAMES.ORGANIZATIONS,
    icon: 'building-office',
    order: 8,
    requiresAuth: true,
  },
  {
    name: 'Documentation',
    route: ROUTE_NAMES.DOCUMENTATION,
    icon: 'book',
    order: 9,
    requiresAuth: true,
  },
];

export const ADMIN_NAVIGATION_ITEMS: NavItem[] = [
  {
    name: 'Admin Dashboard',
    route: ROUTE_NAMES.ADMIN_DASHBOARD,
    icon: 'admin-dashboard',
    order: 1,
    requiredRole: 'admin',
  },
  {
    name: 'User Management',
    route: ROUTE_NAMES.ADMIN_USERS,
    icon: 'users',
    order: 2,
    requiredRole: 'admin',
  },
  {
    name: 'Admin Settings',
    route: ROUTE_NAMES.ADMIN_SETTINGS,
    icon: 'settings',
    order: 3,
    requiredRole: 'admin',
  },
];
